import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSocket } from "@/context/SocketContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, MessageCircle, Sparkles, LogOut, User, Compass, Flame, ShieldCheck, Wallet, Radio, Video, ChevronDown, CalendarHeart, LifeBuoy, Menu, History } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useUnreadCount, UnreadBadge } from "@/lib/unread";

const MobileItem = ({ to, Icon, label, testid, gold, badge }) => (
  <DropdownMenuItem asChild>
    <Link to={to} data-testid={testid} className="flex items-center gap-3 py-2.5 cursor-pointer">
      <Icon size={16} className={gold ? "text-gold" : "text-text-muted"} /> <span className="flex-1">{label}</span>
      {badge > 0 && <span className="min-w-[18px] h-[18px] px-1 bg-wine text-white text-[10px] rounded-full flex items-center justify-center">{badge}</span>}
    </Link>
  </DropdownMenuItem>
);

const MobileMore = ({ user, ticketCount, onLogout }) => {
  const alert = (user.role === "admin" && ticketCount > 0) || (user.role !== "admin" && !user.age_verified);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-more" aria-label="More"
        className="md:hidden relative w-11 h-11 -mr-2 flex items-center justify-center rounded-full text-text-main outline-none active:bg-white/10">
        <Menu size={22} strokeWidth={1.5} />
        {alert && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-wine animate-pulse" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="bg-surface border-white/10 text-text-main w-[min(18rem,calc(100vw-2rem))]">
        <DropdownMenuLabel className="text-text-muted text-[10px] uppercase tracking-[0.15em]">More</DropdownMenuLabel>
        <MobileItem to="/likes" Icon={Heart} label="Admirers" testid="more-likes" />
        <MobileItem to="/events" Icon={CalendarHeart} label="Events" testid="more-events" />
        <MobileItem to="/models" Icon={Radio} label="Velluto Models" testid="more-models" gold />
        <MobileItem to={user.is_creator ? "/creator/register" : "/apply"} Icon={Video} label={user.is_creator ? "Creator studio" : "Become a Creator"} testid="more-become-creator" />
        {!user.is_premium && <MobileItem to="/premium" Icon={Sparkles} label="Premium" testid="more-premium" gold />}
        {user.role !== "admin" && !user.age_verified && <MobileItem to="/verify" Icon={ShieldCheck} label="Verify your age" testid="more-verify" gold />}
        {user.role === "admin" && <MobileItem to="/admin" Icon={ShieldCheck} label="Admin" testid="more-admin" gold badge={ticketCount} />}
        <DropdownMenuSeparator className="bg-white/10" />
        <MobileItem to="/security/sign-ins" Icon={History} label="Sign-in history" testid="more-signins" />
        <MobileItem to="/support" Icon={LifeBuoy} label="Help & Support" testid="more-support" />
        <DropdownMenuItem data-testid="more-logout" onClick={onLogout} className="flex items-center gap-3 py-2.5 cursor-pointer text-text-muted">
          <LogOut size={16} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { addAdminListener } = useSocket();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.role === "admin";
  const [ticketCount, setTicketCount] = useState(0);
  const unread = useUnreadCount();

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const load = () => api.get("/admin/tickets/unread_count").then(({ data }) => alive && setTicketCount(data.count)).catch(() => {});
    load();
    const poll = setInterval(load, 60000);
    const off = addAdminListener((data) => {
      if (data.type === "support_ticket") {
        setTicketCount((c) => c + 1);
        toast(`New support ticket: ${data.ticket.subject}`, {
          description: `From ${data.ticket.user_name}`,
          action: { label: "Open", onClick: () => nav("/admin") },
        });
      }
    });
    // refresh count when navigating away from the dashboard (statuses may have changed)
    return () => { alive = false; clearInterval(poll); off(); };
  }, [isAdmin, addAdminListener, nav]);

  useEffect(() => {
    if (isAdmin && loc.pathname !== "/admin") {
      api.get("/admin/tickets/unread_count").then(({ data }) => setTicketCount(data.count)).catch(() => {});
    }
  }, [loc.pathname, isAdmin]);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const link = (to, label, Icon, testid, extra = "", badge = 0) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testid}
        className={`${extra} relative flex items-center gap-2 text-sm tracking-wide transition-colors ${
          active ? "text-text-main" : "text-text-muted hover:text-text-main"
        }`}
      >
        <Icon size={16} strokeWidth={1.5} />
        <span className="hidden md:inline">{label}</span>
        <UnreadBadge count={badge} testid={`${testid}-badge`} className="absolute -top-2.5 left-2.5" />
      </Link>
    );
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink/70 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/hub" : "/"} data-testid="nav-logo" className="brand-script text-[2.1rem] sm:text-[2.7rem] pt-2 leading-[1.15] shrink-0">
          Velluto
        </Link>
        {user ? (
          <>
          <MobileMore user={user} ticketCount={ticketCount} onLogout={doLogout} />
          <nav className="hidden md:flex items-center gap-8">
            {link("/browse", "Discover", Compass, "nav-browse")}
            {link("/matches", "Matches", Flame, "nav-matches")}
            {link("/likes", "Admirers", Heart, "nav-likes")}
            {link("/messages", "Messages", MessageCircle, "nav-messages", "", unread)}
            {link("/events", "Events", CalendarHeart, "nav-events")}
            {link("/wallet", "Wallet", Wallet, "nav-wallet")}
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-creators-menu" className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-main transition-colors outline-none">
                <Video size={16} strokeWidth={1.5} />
                <span className="hidden md:inline">Creators</span>
                <ChevronDown size={12} className="hidden md:inline" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface border-white/10 text-text-main min-w-[220px]">
                <DropdownMenuLabel className="text-text-muted text-[10px] uppercase tracking-[0.15em]">Content creators</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/models" data-testid="nav-models" className="flex items-center gap-2 cursor-pointer">
                    <Radio size={14} className="text-gold" /> Velluto Models
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem asChild>
                  <Link to={user?.is_creator ? "/creator/register" : "/apply"} data-testid="nav-become-creator" className="flex items-center gap-2 cursor-pointer">
                    <Video size={14} className="text-gold" /> Become a Creator
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/support" data-testid="nav-support" className="flex items-center gap-2 cursor-pointer">
                    <LifeBuoy size={14} className="text-gold" /> Help & Support
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {link("/me", "Profile", User, "nav-profile")}
            {user.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin"
                className={`relative flex items-center gap-2 text-sm tracking-wide transition-colors ${loc.pathname === "/admin" ? "text-text-main" : "text-text-muted hover:text-text-main"}`}>
                <ShieldCheck size={16} strokeWidth={1.5} />
                <span className="hidden md:inline">Admin</span>
                {ticketCount > 0 && (
                  <span data-testid="admin-ticket-badge" className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 bg-wine text-white text-[10px] rounded-full flex items-center justify-center leading-none">
                    {ticketCount}
                  </span>
                )}
              </Link>
            )}
            {user.role !== "admin" && !user.age_verified && (
              <Link to="/verify" data-testid="nav-verify"
                className="flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors">
                <span className="relative flex"><ShieldCheck size={16} strokeWidth={1.5} /><span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-wine animate-pulse" /></span>
                <span className="hidden md:inline">Verify</span>
              </Link>
            )}
            {!user.is_premium && (
              <Link
                to="/premium"
                data-testid="nav-premium"
                className="flex items-center gap-2 text-sm text-gold hover:text-gold/80 transition-colors"
              >
                <Sparkles size={16} strokeWidth={1.5} />
                <span className="hidden md:inline">Premium</span>
              </Link>
            )}
            <button data-testid="nav-logout" onClick={doLogout} className="text-text-muted hover:text-text-main transition-colors">
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </nav>
          </>
        ) : (
          <nav className="flex items-center gap-4 sm:gap-6">
            <Link to="/auth" data-testid="nav-login" className="whitespace-nowrap text-sm text-text-muted hover:text-text-main transition-colors">
              Sign in
            </Link>
            <Link
              to="/auth?mode=register"
              data-testid="nav-join"
              className="whitespace-nowrap px-4 sm:px-6 py-2.5 bg-wine hover:bg-wine-hover transition-colors text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] rounded-full"
            >
              Request access
            </Link>
          </nav>
        )}
      </div>
    </header>
  );
}
